import React, { lazy, Suspense, useContext, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Loading from './components/Loading';
import User from './pages/User';
import { PrivateRoute } from './components/PrivateRoute';
import RegisterForm from './components/RegisterForm';
import LoginForm from './components/LoginForm';
import NotFound from './pages/NotFound';
import Setting from './pages/Setting';
import EditUser from './pages/User/EditUser_v2';
import AddUser from './pages/User/AddUser';
import { API, isRoot, showError, showNotice } from './helpers';
import PasswordResetForm from './components/PasswordResetForm';
import GitHubOAuth from './components/GitHubOAuth';
import PasswordResetConfirm from './components/PasswordResetConfirm';
import { UserContext } from './context/User';
import { StatusContext } from './context/Status';
import { readCachedUserV104 } from './helpers/authSessionV104';
import Channel from './pages/Channel';
import Token from './pages/Token';
import EditToken from './pages/Token/EditToken';
import EditChannel from './pages/Channel/EditChannel';
import Redemption from './pages/Redemption';
import EditRedemption from './pages/Redemption/EditRedemption';
import Log from './pages/Log';
import Chat from './pages/Chat';
import LarkOAuth from './components/LarkOAuth';
import Dashboard from './pages/Dashboard';
import Model from './pages/Model';
import Organization from './pages/Organization';
import Billing from './pages/Billing';
import Notification from './pages/Notification';
import VertexLogV83 from './pages/VertexLogV83';
import CustomerVertexLogV811 from './pages/CustomerVertexLogV811';
import OrganizationLogV822 from './pages/OrganizationLogV822';
import ConsumptionExportV1014 from './pages/ConsumptionExportV1014';
import { CUSTOMER_LOG_ROUTES_V822 } from './components/portalNavigationV811';

const Home = lazy(() => import('./pages/Home/index_v101'));
const PublicHomeV101 = lazy(() => import('./pages/Home/PublicHomeV101'));
const About = lazy(() => import('./pages/About'));
const Documentation = lazy(() => import('./pages/Documentation'));
const AccessAuditV101 = lazy(() => import('./pages/AccessAuditV101'));
const RoutingPoliciesV104 = lazy(() => import('./pages/RoutingPoliciesV104'));
const CommercialV106 = lazy(() => import('./pages/CommercialV106'));
const SourceMultipliersV108 = lazy(() =>
  import('./pages/SourceMultipliersV108')
);
const BasePricesV108 = lazy(() => import('./pages/BasePricesV108'));
const ClientAuthorizeV110 = lazy(() => import('./pages/ClientAuthorizeV110'));
const CodingAppsV115 = lazy(() => import('./pages/CodingAppsV115'));
const StaffV110 = lazy(() => import('./pages/StaffV110'));

function LegacyPricingRedirectV108({ to }) {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
}

function RootLogRouteV822({ children }) {
  return (
    <PrivateRoute>
      {isRoot() ? (
        children
      ) : (
        <Navigate to={CUSTOMER_LOG_ROUTES_V822.ordinary} replace />
      )}
    </PrivateRoute>
  );
}

function LegacyCustomerLogRedirectV822() {
  const { search, hash } = useLocation();
  return (
    <Navigate
      to={{ pathname: CUSTOMER_LOG_ROUTES_V822.special, search, hash }}
      replace
    />
  );
}

function App() {
  const [, userDispatch] = useContext(UserContext);
  const [, statusDispatch] = useContext(StatusContext);

  const loadUser = () => {
    const user = readCachedUserV104();
    if (user) {
      userDispatch({ type: 'login', payload: user });
    }
  };
  const loadStatus = async () => {
    try {
      const res = await API.get('/api/status');
      if (!res?.data) return;
      const { success, message, data } = res.data || {}; // Add default empty object
      if (success && data) {
        // Check data exists
        localStorage.setItem('status', JSON.stringify(data));
        statusDispatch({ type: 'set', payload: data });
        localStorage.setItem('system_name', data.system_name);
        localStorage.setItem('logo', data.logo);
        localStorage.setItem('footer_html', data.footer_html);
        localStorage.setItem('quota_per_unit', data.quota_per_unit);
        localStorage.setItem('display_in_currency', data.display_in_currency);
        if (data.chat_link) {
          localStorage.setItem('chat_link', data.chat_link);
        } else {
          localStorage.removeItem('chat_link');
        }
        if (
          data.version !== process.env.REACT_APP_VERSION &&
          data.version !== 'v0.0.0' &&
          process.env.REACT_APP_VERSION !== ''
        ) {
          showNotice(
            `新版本可用：${data.version}，请使用快捷键 Shift + F5 刷新页面`
          );
        }
      } else {
        showError(message || '无法正常连接至服务器！');
      }
    } catch (error) {
      showError(error.message || '无法正常连接至服务器！');
    }
  };

  useEffect(() => {
    loadUser();
    loadStatus().then();
  }, []);

  return (
    <Routes>
      <Route path='/coding-apps' element={<PrivateRoute><Suspense fallback={<Loading />}><CodingAppsV115 /></Suspense></PrivateRoute>} />
      <Route path='/staff/*' element={<PrivateRoute><Suspense fallback={<Loading />}><StaffV110 /></Suspense></PrivateRoute>} />
      <Route path='/client/authorize' element={<Suspense fallback={<Loading />}><ClientAuthorizeV110 /></Suspense>} />
      <Route
        path='/route-offers'
        element={
          <PrivateRoute>
            <LegacyPricingRedirectV108 to='/source-multipliers' />
          </PrivateRoute>
        }
      />
      <Route
        path='/commercial'
        element={
          <PrivateRoute>
            <LegacyPricingRedirectV108 to='/base-prices' />
          </PrivateRoute>
        }
      />
      <Route
        path='/base-prices'
        element={
          <PrivateRoute>
            <Suspense fallback={<Loading />}>
              <BasePricesV108 />
            </Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path='/source-multipliers'
        element={
          <PrivateRoute>
            <Suspense fallback={<Loading />}>
              <SourceMultipliersV108 />
            </Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path='/source-multipliers/model'
        element={
          <PrivateRoute>
            <Suspense fallback={<Loading />}>
              <CommercialV106 />
            </Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path='/routing'
        element={
          <PrivateRoute>
            <Suspense fallback={<Loading />}>
              <RoutingPoliciesV104 />
            </Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path='/home'
        element={
          <Suspense fallback={<Loading />}>
            <PublicHomeV101 />
          </Suspense>
        }
      />
      <Route
        path='/'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <Home />
          </Suspense>
        }
      />
      <Route
        path='/channel'
        element={
          <PrivateRoute>
            <Channel />
          </PrivateRoute>
        }
      />
      <Route
        path='/channel/edit/:id'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditChannel />
          </Suspense>
        }
      />
      <Route
        path='/channel/add'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditChannel />
          </Suspense>
        }
      />
      <Route
        path='/token'
        element={
          <PrivateRoute>
            <Token />
          </PrivateRoute>
        }
      />
      <Route
        path='/access-audit-v101'
        element={
          <RootLogRouteV822>
            <Suspense fallback={<Loading />}>
              <AccessAuditV101 />
            </Suspense>
          </RootLogRouteV822>
        }
      />
      <Route
        path='/organization'
        element={
          <PrivateRoute>
            <Organization />
          </PrivateRoute>
        }
      />
      <Route
        path='/organization/:organizationId'
        element={
          <PrivateRoute>
            <Organization />
          </PrivateRoute>
        }
      />
      <Route
        path='/organization/:organizationId/user/:userId'
        element={
          <PrivateRoute>
            <Organization />
          </PrivateRoute>
        }
      />
      <Route
        path='/organization/:organizationId/user/:userId/token/add'
        element={
          <PrivateRoute>
            <Suspense fallback={<Loading></Loading>}>
              <EditToken />
            </Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path='/organization/:organizationId/user/:userId/token/edit/:id'
        element={
          <PrivateRoute>
            <Suspense fallback={<Loading></Loading>}>
              <EditToken />
            </Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path='/billing'
        element={
          <PrivateRoute>
            <Billing />
          </PrivateRoute>
        }
      />
      <Route
        path='/notification'
        element={
          <PrivateRoute>
            <Notification />
          </PrivateRoute>
        }
      />
      <Route
        path='/model'
        element={
          <PrivateRoute>
            <Model />
          </PrivateRoute>
        }
      />
      <Route
        path='/docs'
        element={
          <PrivateRoute>
            <Suspense fallback={<Loading></Loading>}>
              <Documentation />
            </Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path='/token/edit/:id'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditToken />
          </Suspense>
        }
      />
      <Route
        path='/token/add'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditToken />
          </Suspense>
        }
      />
      <Route
        path='/redemption'
        element={
          <PrivateRoute>
            <Redemption />
          </PrivateRoute>
        }
      />
      <Route
        path='/redemption/edit/:id'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditRedemption />
          </Suspense>
        }
      />
      <Route
        path='/redemption/add'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditRedemption />
          </Suspense>
        }
      />
      <Route
        path='/user'
        element={
          <PrivateRoute>
            <User />
          </PrivateRoute>
        }
      />
      <Route
        path='/user/edit/:id'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditUser />
          </Suspense>
        }
      />
      <Route
        path='/user/edit'
        element={
          <PrivateRoute>
            <Navigate to='/setting' replace />
          </PrivateRoute>
        }
      />
      <Route
        path='/user/add'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <AddUser />
          </Suspense>
        }
      />
      <Route
        path='/user/reset'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <PasswordResetConfirm />
          </Suspense>
        }
      />
      <Route
        path='/login'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <LoginForm />
          </Suspense>
        }
      />
      <Route
        path='/register'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <RegisterForm />
          </Suspense>
        }
      />
      <Route
        path='/reset'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <PasswordResetForm />
          </Suspense>
        }
      />
      <Route
        path='/oauth/github'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <GitHubOAuth />
          </Suspense>
        }
      />
      <Route
        path='/oauth/lark'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <LarkOAuth />
          </Suspense>
        }
      />
      <Route
        path='/setting'
        element={
          <PrivateRoute>
            <Suspense fallback={<Loading></Loading>}>
              <Setting />
            </Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path='/topup'
        element={
          <PrivateRoute>
            <Navigate to='/billing' replace />
          </PrivateRoute>
        }
      />
      <Route
        path='/consumption-list'
        element={
          <PrivateRoute>
            <ConsumptionExportV1014 />
          </PrivateRoute>
        }
      />
      <Route
        path={CUSTOMER_LOG_ROUTES_V822.ordinary}
        element={
          <PrivateRoute>
            <Log />
          </PrivateRoute>
        }
      />
      <Route
        path={CUSTOMER_LOG_ROUTES_V822.legacy}
        element={
          <RootLogRouteV822>
            <LegacyCustomerLogRedirectV822 />
          </RootLogRouteV822>
        }
      />
      <Route
        path={CUSTOMER_LOG_ROUTES_V822.special}
        element={
          <RootLogRouteV822>
            <OrganizationLogV822 />
          </RootLogRouteV822>
        }
      />
      <Route
        path={CUSTOMER_LOG_ROUTES_V822.googleDiagnostics}
        element={
          <RootLogRouteV822>
            <VertexLogV83 />
          </RootLogRouteV822>
        }
      />
      <Route
        path={CUSTOMER_LOG_ROUTES_V822.customer}
        element={
          <PrivateRoute>
            <CustomerVertexLogV811 />
          </PrivateRoute>
        }
      />
      <Route
        path='/about'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <About />
          </Suspense>
        }
      />
      <Route
        path='/chat'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <Chat />
          </Suspense>
        }
      />
      <Route
        path='/dashboard'
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route path='*' element={<NotFound />} />
    </Routes>
  );
}

export default App;
